import { useEffect, useMemo, useState } from 'react';

import {
  AnswerFieldsFragment,
  AnswerWhereInput,
  ListAnswersWithUsersDocument,
  ListAnswersWithUsersQuery,
  ListAnswersWithUsersQueryVariables,
  OrderByArgs,
  PaginationArgs,
} from '@/@generated/codegen/API';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { captureSentry } from '@/utils/helpers/sentryHelper';

export function useAnswersByMeetingID(meetingID: string | null | undefined) {
  const { apolloClient } = useApolloClientContext();
  const [answerList, setAnswerList] = useState<AnswerFieldsFragment[]>([]);
  const where: AnswerWhereInput = useMemo(() => {
    return {
      meetingId: {
        equals: meetingID,
      },
    };
  }, [meetingID]);
  const [loading, setLoading] = useState<boolean>(false);

  // NIKO TODO: use useEntityFetchAll instead of this
  const fetchAllAnswers = async () => {
    if (!meetingID) return;
    setLoading(true);

    let hasMore = true;
    let cursor: string | undefined = undefined;
    let allAnswers: AnswerFieldsFragment[] = [];

    while (hasMore) {
      const query = await apolloClient.query<
        ListAnswersWithUsersQuery,
        ListAnswersWithUsersQueryVariables
      >({
        query: ListAnswersWithUsersDocument,
        variables: {
          orderByArgs: {
            orderByField: 'createdAt',
            direction: 'desc',
          } satisfies OrderByArgs,
          paginationArgs: {
            first: 100,
            after: cursor,
          } satisfies PaginationArgs,
          where,
        },
        fetchPolicy: 'network-only',
      });

      const data: ListAnswersWithUsersQuery = query.data;

      const answers = data.listAnswers.edges.map((el) => el.node);
      allAnswers = [...allAnswers, ...answers];

      const pageInfo = data.listAnswers.pageInfo;
      cursor = pageInfo.endCursor ?? undefined;
      hasMore = pageInfo.hasNextPage;
    }

    setAnswerList(allAnswers);
    setLoading(false);
  };

  useEffect(() => {
    void fetchAllAnswers();
  }, [meetingID]);

  useEntitySubscription('Answer', {
    onCreate: {
      variables: {
        where,
      },
      onData: (res) => {
        const obj = res.data.data?.onCreateAnswer;

        if (!obj) {
          captureSentry({
            title: 'useAnswersByMeetingID | onCreate | onData | obj is undefined',
            detail: {},
          });
          return;
        }
        setAnswerList((prev) => [...prev, obj]);
      },
    },
    onUpdate: {
      variables: {
        where,
      },
      onData: (res) => {
        const obj = res.data.data?.onUpdateAnswer;

        if (!obj) {
          captureSentry({
            title: 'useAnswersByMeetingID | onUpdate | onData | obj is undefined',
            detail: {},
          });
          return;
        }
        setAnswerList((prev) => prev.map((el) => (el.id === obj.id ? { ...el, ...obj } : el)));
      },
    },
    onDelete: {
      variables: {},
      onData: (res) => {
        const obj = res.data.data?.onDeleteAnswer;

        if (!obj) {
          captureSentry({
            title: 'useAnswersByMeetingID | onDelete | onData | obj is undefined',
            detail: {},
          });
          return;
        }
        if (obj.meetingId !== meetingID) return;
        setAnswerList((prev) => prev.filter((a) => a.id !== obj.id));
      },
    },
  });

  return {
    answerList,
    isLoading: loading,
  };
}
